import { useState } from "react";
import axios from "axios";

import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

import "./04_teachersform.css";

const INITIAL_FORM = {
  firstname: "",
  lastname: "",
  email: "",
  phone: "",
  description: "",
  experience: "",
  services: [],
  headline: "",
  instruments: "",
  language: "",
  city: "",
  fees: "",
  gender: "",
  teaching_mode: [],
  profilePhoto: null,
};

const servicesList = [
  "Online Classes",
  "Offline Classes",
  "Home Tuition",
  "Exam Preparation",
  "Competition Coaching",
  "Performance Coaching",
  "Music Theory",
  "Beginner Lessons",
  "Advanced Lessons",
  "Children Classes",
];

const teachingModes = ["Online", "Offline", "Can Travel"];

export default function TeachersForm() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitStatus, setSubmitStatus] = useState("");
  const [photoPreview, setPhotoPreview] = useState(null);

  const validateField = (name, value = form[name]) => {
    let error = "";

    switch (name) {
      case "firstname":
        if (!value.trim()) {
          error = "First name is required.";
        } else if (value.trim().length < 2) {
          error = "Please enter at least 2 characters.";
        } else if (!/^[a-zA-ZÀ-ÿ\s'-]+$/.test(value)) {
          error = "Please enter a valid first name.";
        }
        break;

      case "lastname":
        if (!value.trim()) {
          error = "Last name is required.";
        } else if (value.trim().length < 2) {
          error = "Please enter at least 2 characters.";
        } else if (!/^[a-zA-ZÀ-ÿ\s'-]+$/.test(value)) {
          error = "Please enter a valid last name.";
        }
        break;

      case "email":
        if (!value.trim()) {
          error = "Email address is required.";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
          error = "Please enter a valid email address.";
        }
        break;

      case "phone":
        if (!value) {
          error = "Phone number is required.";
        }
        break;

      case "description":
        if (value.length > 750) {
          error = "Description cannot exceed 750 characters.";
        }
        break;

      case "experience":
        if (value.length > 450) {
          error = "Experience cannot exceed 450 characters.";
        }
        break;

      case "services":
        if (value.length > 5) {
          error = "You can select a maximum of 5 services.";
        }
        break;

      case "headline":
        if (value.length > 50) {
          error = "Headline cannot exceed 50 characters.";
        }
        break;

      case "instruments":
        if (!value.trim()) {
          error = "Please enter the instruments you teach.";
        }
        break;

      case "language":
        if (!value.trim()) {
          error = "Please enter the language(s) you teach in.";
        }
        break;

      case "city":
        if (!value.trim()) {
          error = "Please enter your city.";
        }
        break;

      case "fees":
        if (!value.trim()) {
          error = "Please enter your hourly fee.";
        } else if (!/^\d+(\.\d{1,2})?$/.test(value.trim())) {
          error = "Please enter a valid fee, e.g. 500.";
        } else if (Number(value) <= 0) {
          error = "Fee must be greater than 0.";
        }
        break;

      case "gender":
        if (!value) {
          error = "Please select your gender.";
        }
        break;

      case "profilePhoto":
        if (value) {
          const allowedTypes = ["image/jpeg", "image/png", "image/webp"];

          if (!allowedTypes.includes(value.type)) {
            error = "Please upload a JPG, PNG, or WebP image.";
          } else if (value.size > 5 * 1024 * 1024) {
            error = "Profile photo must be smaller than 5 MB.";
          }
        }
        break;

      default:
        break;
    }

    return error;
  };

  const validateForm = () => {
    const fieldsToValidate = [
      "firstname",
      "lastname",
      "email",
      "phone",
      "description",
      "experience",
      "services",
      "headline",
      "instruments",
      "language",
      "city",
      "fees",
      "gender",
      "profilePhoto",
    ];

    const errors = {};

    fieldsToValidate.forEach((field) => {
      const error = validateField(field);
      if (error) {
        errors[field] = error;
      }
    });

    if (form.services.length > 5) {
      errors.services = "You can select a maximum of 5 services.";
    }

    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (touched[name]) {
      const error = validateField(name, value);

      setFormErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const error = validateField(name);

    setFormErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handlePhoneChange = (value) => {
    setForm((prev) => ({
      ...prev,
      phone: value || "",
    }));

    if (touched.phone) {
      setFormErrors((prev) => ({
        ...prev,
        phone: value ? "" : "Phone number is required.",
      }));
    }
  };

  const handleServiceChange = (e) => {
    const { value, checked } = e.target;

    setForm((prev) => {
      let updated = [...prev.services];

      if (checked) {
        if (updated.length >= 5) {
          setFormErrors((errors) => ({
            ...errors,
            services: "You can select a maximum of 5 services.",
          }));

          return prev;
        }

        updated.push(value);
      } else {
        updated = updated.filter((item) => item !== value);
      }

      return {
        ...prev,
        services: updated,
      };
    });

    setTouched((prev) => ({
      ...prev,
      services: true,
    }));

    setFormErrors((prev) => ({
      ...prev,
      services: "",
    }));
  };

  const handleTeachingModeChange = (e) => {
    const { value, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      teaching_mode: checked
        ? [...prev.teaching_mode, value]
        : prev.teaching_mode.filter((item) => item !== value),
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];

    setForm((prev) => ({
      ...prev,
      profilePhoto: file || null,
    }));

    if (!file) {
      setPhotoPreview(null);
      return;
    }

    const error = validateField("profilePhoto", file);

    setFormErrors((prev) => ({
      ...prev,
      profilePhoto: error,
    }));

    if (!error) {
      const reader = new FileReader();

      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };

      reader.readAsDataURL(file);
    } else {
      setPhotoPreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitMessage("");
    setSubmitStatus("");

    const errors = validateForm();

    setFormErrors(errors);

    setTouched({
      firstname: true,
      lastname: true,
      email: true,
      phone: true,
      description: true,
      experience: true,
      services: true,
      headline: true,
      instruments: true,
      language: true,
      city: true,
      fees: true,
      gender: true,
      profilePhoto: true,
    });

    if (Object.keys(errors).length > 0) {
      const firstError = Object.keys(errors)[0];

      document.querySelector(`[name="${firstError}"]`)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();

      Object.entries(form).forEach(([key, value]) => {
        if (value === null) return;

        if (Array.isArray(value)) {
          value.forEach((item) => {
            formData.append(`${key}[]`, item);
          });
        } else {
          formData.append(key, value);
        }
      });

      const res = await axios.post(
        "https://sursaathi.in/teacherslisting/emailshandler.php",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (res.data.success) {
        setSubmitStatus("success");
        setSubmitMessage(
          res.data.message || "Your profile has been created successfully!",
        );

        setForm(INITIAL_FORM);
        setTouched({});
        setFormErrors({});
        setPhotoPreview(null);

        window.scrollTo({
          top:
            document.getElementById("teachersregistrationform")?.offsetTop -
              100 || 0,
          behavior: "smooth",
        });
      } else {
        setSubmitStatus("error");
        setFormErrors(res.data.errors || {});
        setSubmitMessage(
          res.data.message || "Please correct the errors below.",
        );
      }
    } catch (err) {
      setSubmitStatus("error");
      setSubmitMessage(
        err.response?.data?.message ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderError = (field) => {
    if (!formErrors[field]) return null;

    return (
      <span className="field-error" role="alert">
        {formErrors[field]}
      </span>
    );
  };

  const getInputClass = (field) => {
    if (!touched[field]) return "";

    return formErrors[field] ? "has-error" : "has-success";
  };

  return (
    <section className="TeachersRegistrationPage">
      <div id="teachersregistrationform"></div>

      <div className="TeachersRegistrationPage-container">
        <div className="TeachersRegistrationPage-heading">
          <span className="form-eyebrow">TEACHER REGISTRATION</span>

          <h2 className="TeachersRegistationPage-Form-title">
            Create Your Free Profile
          </h2>
        </div>

        {submitMessage && (
          <div className={`form-message ${submitStatus}`} role="alert">
            <span className="message-icon">
              {submitStatus === "success" ? "✓" : "!"}
            </span>

            <span>{submitMessage}</span>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="TeachersRegistationPage-Form"
          noValidate
        >
          {/* PERSONAL INFORMATION */}

          <div className="form-section">
            <div className="section-heading">
              <span className="section-number">01</span>

              <div>
                <h3>Personal Information</h3>
                <p>Basic details students will see on your profile.</p>
              </div>
            </div>

            <div className="form-grid two-columns">
              <div className="form-field">
                <label htmlFor="firstname">
                  First Name <span>*</span>
                </label>

                <input
                  id="firstname"
                  className={getInputClass("firstname")}
                  type="text"
                  name="firstname"
                  value={form.firstname}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="e.g. Arjun"
                  autoComplete="given-name"
                />

                {renderError("firstname")}
              </div>

              <div className="form-field">
                <label htmlFor="lastname">
                  Last Name <span>*</span>
                </label>

                <input
                  id="lastname"
                  className={getInputClass("lastname")}
                  type="text"
                  name="lastname"
                  value={form.lastname}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="e.g. Sharma"
                  autoComplete="family-name"
                />

                {renderError("lastname")}
              </div>

              <div className="form-field">
                <label htmlFor="phone">
                  Phone Number <span>*</span>
                </label>

                <PhoneInput
                  id="phone"
                  international
                  defaultCountry="IN"
                  value={form.phone}
                  onChange={handlePhoneChange}
                  onBlur={() => {
                    setTouched((prev) => ({
                      ...prev,
                      phone: true,
                    }));
                  }}
                  placeholder="Enter phone number"
                  className={
                    formErrors.phone && touched.phone ? "has-error" : ""
                  }
                />

                {renderError("phone")}
              </div>

              <div className="form-field">
                <label htmlFor="email">
                  Email Address <span>*</span>
                </label>

                <input
                  id="email"
                  className={getInputClass("email")}
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="you@example.com"
                  autoComplete="email"
                />

                {renderError("email")}
              </div>
            </div>
          </div>

          {/* PROFILE PHOTO */}

          <div className="form-section">
            <div className="section-heading">
              <span className="section-number">02</span>

              <div>
                <h3>Profile Photo</h3>
                <p>
                  A clear photo helps students feel more comfortable choosing
                  you.
                </p>
              </div>
            </div>

            <div className="photo-upload">
              <div className="photo-preview">
                {photoPreview ? (
                  <img src={photoPreview} alt="Profile preview" />
                ) : (
                  <span>+</span>
                )}
              </div>

              <div className="photo-upload-content">
                <label htmlFor="profilePhoto" className="upload-button">
                  Choose Photo
                </label>

                <input
                  id="profilePhoto"
                  type="file"
                  name="profilePhoto"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handlePhotoChange}
                  hidden
                />

                <p>JPG, PNG or WebP · Maximum 5 MB</p>

                {renderError("profilePhoto")}
              </div>
            </div>
          </div>

          {/* ABOUT YOU */}

          <div className="form-section">
            <div className="section-heading">
              <span className="section-number">03</span>

              <div>
                <h3>About You</h3>
                <p>Give students a reason to choose you as their teacher.</p>
              </div>
            </div>

            <div className="form-field">
              <div className="label-row">
                <label htmlFor="headline">Profile Headline</label>

                <span className="character-count">
                  {form.headline.length}/50
                </span>
              </div>

              <input
                id="headline"
                className={getInputClass("headline")}
                type="text"
                name="headline"
                value={form.headline}
                maxLength={50}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g. Experienced Hindustani Vocal Teacher"
              />

              {renderError("headline")}
            </div>

            <div className="form-field">
              <div className="label-row">
                <label htmlFor="description">Your Description</label>

                <span className="character-count">
                  {form.description.length}/750
                </span>
              </div>

              <textarea
                id="description"
                className={getInputClass("description")}
                name="description"
                value={form.description}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={750}
                placeholder="Introduce yourself, your teaching style and what makes your classes special..."
              />

              {renderError("description")}
            </div>

            <div className="form-field">
              <div className="label-row">
                <label htmlFor="experience">Teaching Experience</label>

                <span className="character-count">
                  {form.experience.length}/450
                </span>
              </div>

              <textarea
                id="experience"
                className={getInputClass("experience")}
                name="experience"
                value={form.experience}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={450}
                placeholder="Tell students about your teaching experience, certifications, performances, etc."
              />

              {renderError("experience")}
            </div>
          </div>

          {/* SERVICES */}

          <div className="form-section">
            <div className="section-heading">
              <span className="section-number">04</span>

              <div>
                <h3>Services You Offer</h3>
                <p>Choose up to 5 services that best describe your classes.</p>
              </div>

              <span className="selection-count">{form.services.length}/5</span>
            </div>

            <div className="service-grid">
              {servicesList.map((service) => {
                const selected = form.services.includes(service);

                return (
                  <label
                    key={service}
                    className={`service-option ${selected ? "selected" : ""}`}
                  >
                    <input
                      type="checkbox"
                      value={service}
                      checked={selected}
                      onChange={handleServiceChange}
                    />

                    <span className="custom-checkbox">
                      {selected ? "✓" : ""}
                    </span>

                    <span>{service}</span>
                  </label>
                );
              })}
            </div>

            {renderError("services")}
          </div>

          {/* TEACHING DETAILS */}

          <div className="form-section">
            <div className="section-heading">
              <span className="section-number">05</span>

              <div>
                <h3>Teaching Details</h3>
                <p>Help students understand what and where you teach.</p>
              </div>
            </div>

            <div className="form-grid two-columns">
              <div className="form-field">
                <label htmlFor="instruments">
                  Instruments / Subjects <span>*</span>
                </label>

                <input
                  id="instruments"
                  className={getInputClass("instruments")}
                  type="text"
                  name="instruments"
                  value={form.instruments}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="e.g. Guitar, Piano"
                />

                {renderError("instruments")}
              </div>

              <div className="form-field">
                <label htmlFor="language">
                  Teaching Language <span>*</span>
                </label>

                <input
                  id="language"
                  className={getInputClass("language")}
                  type="text"
                  name="language"
                  value={form.language}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="e.g. English, Hindi"
                />

                {renderError("language")}
              </div>

              <div className="form-field">
                <label htmlFor="city">
                  Location / City <span>*</span>
                </label>

                <input
                  id="city"
                  className={getInputClass("city")}
                  type="text"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="e.g. Mumbai"
                  autoComplete="address-level2"
                />

                {renderError("city")}
              </div>

              <div className="form-field">
                <label htmlFor="fees">
                  Hourly Fee <span>*</span>
                </label>

                <div className="fee-input">
                  <span>₹</span>

                  <input
                    id="fees"
                    className={getInputClass("fees")}
                    type="text"
                    inputMode="numeric"
                    name="fees"
                    value={form.fees}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="500"
                  />
                </div>

                {renderError("fees")}
              </div>

              <div className="form-field">
                <label htmlFor="gender">
                  Gender <span>*</span>
                </label>

                <select
                  id="gender"
                  className={getInputClass("gender")}
                  name="gender"
                  value={form.gender}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>

                {renderError("gender")}
              </div>
            </div>

            <div className="form-field teaching-mode-field">
              <label>Teaching Mode</label>

              <div className="mode-grid">
                {teachingModes.map((mode) => {
                  const selected = form.teaching_mode.includes(mode);

                  return (
                    <label
                      key={mode}
                      className={`mode-option ${selected ? "selected" : ""}`}
                    >
                      <input
                        type="checkbox"
                        value={mode}
                        checked={selected}
                        onChange={handleTeachingModeChange}
                      />

                      <span className="mode-icon">
                        {mode === "Online"
                          ? "⌁"
                          : mode === "Offline"
                            ? "⌂"
                            : "→"}
                      </span>

                      <span>{mode}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          {/* SUBMIT */}

          <div className="form-submit-area">
            <p>
              By registering, you agree to provide accurate information for your
              teacher profile.
            </p>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`register-button ${isSubmitting ? "submitting" : ""}`}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Creating Profile...
                </>
              ) : (
                <>
                  Create My Free Profile
                  <span>→</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
